import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export interface EntityMapItem {
  id: string;
  name: string;
  address: string;
  contact?: string;
  email?: string;
  phone?: string;
  status?: string;
  lat?: number | null;
  lng?: number | null;
  detailPath: string;
  metrics?: Array<{ label: string; value: string | number }>;
}

const FitMarkers: React.FC<{ items: EntityMapItem[]; ready: boolean }> = ({ items, ready }) => {
  const map = useMap();
  useEffect(() => {
    if (!ready) return;
    const points = items.filter(i => Number.isFinite(i.lat) && Number.isFinite(i.lng)).map(i => [Number(i.lat), Number(i.lng)] as L.LatLngTuple);
    map.invalidateSize();
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [48, 48], maxZoom: 14 });
  }, [items, map, ready]);
  return null;
};

const MapEntityMarker: React.FC<{
  item: EntityMapItem;
  selected: boolean;
  onHover: () => void;
  onOpen: () => void;
}> = ({ item, selected, onHover, onOpen }) => {
  const map = useMap();
  const keepTooltipVisible = (marker: L.CircleMarker) => {
    window.requestAnimationFrame(() => {
      const tooltip = marker.getTooltip()?.getElement();
      const mapElement = map.getContainer();
      if (!tooltip || !mapElement) return;
      const tooltipBox = tooltip.getBoundingClientRect();
      const mapBox = mapElement.getBoundingClientRect();
      const margin = 14;
      let panX = 0;
      let panY = 0;
      if (tooltipBox.left < mapBox.left + margin) panX = tooltipBox.left - mapBox.left - margin;
      else if (tooltipBox.right > mapBox.right - margin) panX = tooltipBox.right - mapBox.right + margin;
      if (tooltipBox.top < mapBox.top + margin) panY = tooltipBox.top - mapBox.top - margin;
      else if (tooltipBox.bottom > mapBox.bottom - margin) panY = tooltipBox.bottom - mapBox.bottom + margin;
      if (panX || panY) map.panBy([panX, panY], { animate: true, duration: 0.2 });
    });
  };
  return (
    <CircleMarker
      center={[Number(item.lat), Number(item.lng)]}
      radius={selected ? 10 : 7}
      pathOptions={{ color: '#047857', fillColor: selected ? '#10b981' : '#34d399', fillOpacity: .9, weight: 2 }}
      eventHandlers={{
        click: () => onOpen(),
        mouseover: event => {
          onHover();
          keepTooltipVisible(event.target as L.CircleMarker);
        },
      }}
    >
      <Tooltip className="entity-map-tooltip" direction="top" offset={[0, -7]} opacity={1}>
        <strong>{item.name}</strong>
        <span className="entity-map-tooltip-address">{item.address || 'No address on file'}</span>
        {item.metrics?.length ? <div className="entity-map-tooltip-metrics">{item.metrics.map(metric => <span key={metric.label}><small>{metric.label}</small><b>{metric.value ?? 0}</b></span>)}</div> : null}
        {item.contact && <span>{item.contact}</span>}
        {item.status && <span>Status: {item.status}</span>}
      </Tooltip>
    </CircleMarker>
  );
};

export const EntityMapView: React.FC<{ items: EntityMapItem[]; onOpen: (path: string) => void; addressLabel?: string; entityLabel?: string; mapStyle?: 'street' | 'satellite' }> = ({ items, onOpen, addressLabel = 'address', entityLabel, mapStyle = 'street' }) => {
  const [resolved, setResolved] = useState<Record<string, { lat: number; lng: number }>>({});
  const [geocoding, setGeocoding] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const region = process.env.REACT_APP_AWS_LOCATION_REGION || 'us-east-1';
    const index = process.env.REACT_APP_AWS_LOCATION_INDEX || 'intrlock-places';
    const key = process.env.REACT_APP_AWS_LOCATION_KEY || '';
    const missing = items.filter(i => i.address && (!Number.isFinite(i.lat) || !Number.isFinite(i.lng)));
    const geocodeAddress = async (address: string) => {
      if (key) {
        try {
          const response = await fetch(`https://places.geo.${region}.amazonaws.com/places/v0/indexes/${encodeURIComponent(index)}/search/text?key=${encodeURIComponent(key)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ Text: address, MaxResults: 1, FilterCountries: ['USA'] }) });
          if (response.ok) {
            const data = await response.json();
            const point = data?.Results?.[0]?.Place?.Geometry?.Point;
            if (Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1])) return { lng: point[0], lat: point[1] };
          }
        } catch { /* Fall through to the public address lookup. */ }
      }
      try {
        const fallback = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=us&q=${encodeURIComponent(address)}`, { headers: { 'Accept-Language': 'en-US,en;q=0.8' } });
        if (!fallback.ok) return null;
        const results = await fallback.json();
        const lat = Number(results?.[0]?.lat);
        const lng = Number(results?.[0]?.lon);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
      } catch { return null; }
    };
    const run = async () => {
      setGeocoding(missing.length);
      for (const item of missing) {
        if (cancelled) return;
        const cacheKey = `intrlock:geocode:${item.address.trim().toLowerCase()}`;
        try {
          const cached = localStorage.getItem(cacheKey);
          let point: { lat: number; lng: number } | null = null;
          if (cached) {
            const parsed = JSON.parse(cached);
            if (Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng)) point = parsed;
          }
          if (!point) {
            point = await geocodeAddress(item.address);
            if (point) localStorage.setItem(cacheKey, JSON.stringify(point));
          }
          if (point && !cancelled) setResolved(prev => ({ ...prev, [item.id]: point! }));
        } catch { /* A failed address stays in the unmapped count. */ }
        setGeocoding(value => Math.max(0, value - 1));
        await new Promise(resolve => setTimeout(resolve, key ? 120 : 1100));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [items]);
  const locatedItems = useMemo(() => items.map(item => resolved[item.id] ? { ...item, ...resolved[item.id] } : item), [items, resolved]);
  const mapped = useMemo(() => locatedItems.filter(i => Number.isFinite(i.lat) && Number.isFinite(i.lng)), [locatedItems]);
  const missingAddressCount = useMemo(() => locatedItems.filter(i => !i.address).length, [locatedItems]);
  const unlocatedAddressCount = useMemo(() => locatedItems.filter(i => i.address && (!Number.isFinite(i.lat) || !Number.isFinite(i.lng))).length, [locatedItems]);
  const [selectedId, setSelectedId] = useState<string | null>(mapped[0]?.id || items[0]?.id || null);
  useEffect(() => {
    if (!locatedItems.some(i => i.id === selectedId)) setSelectedId(mapped[0]?.id || locatedItems[0]?.id || null);
  }, [locatedItems, mapped, selectedId]);
  const selected = locatedItems.find(i => i.id === selectedId) || null;
  const contactEntityLabel = entityLabel || (selected?.detailPath.startsWith('/integrators/')
    ? 'Dealer'
    : selected?.detailPath.startsWith('/customers/')
      ? 'Customer'
      : selected?.detailPath.startsWith('/properties/')
        ? 'Property'
        : 'Location');
  return (
    <div className="entity-map-view">
      <div className="entity-map-canvas">
        {mapped.length ? (
          <MapContainer center={[39.5, -98.35]} zoom={4} scrollWheelZoom className="h-full w-full">
            <TileLayer
              attribution={mapStyle === 'satellite' ? '&copy; Esri' : '&copy; OpenStreetMap contributors'}
              url={mapStyle === 'satellite' ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
            />
            <FitMarkers items={mapped} ready={geocoding === 0} />
            {mapped.map(item => <MapEntityMarker key={item.id} item={item} selected={item.id === selectedId} onHover={() => setSelectedId(item.id)} onOpen={() => { setSelectedId(item.id); onOpen(item.detailPath); }} />)}
          </MapContainer>
        ) : <div className="entity-map-empty"><strong>No mapped locations</strong><span>Add a valid {addressLabel}; coordinates are calculated automatically.</span></div>}
      </div>
      <aside className="entity-map-detail">
        <small>Selected location</small>
        {selected ? <>
          <h3>{selected.name}</h3><p>{selected.address || 'No address on file'}</p>
          {selected.contact && <div><span>Contact</span><strong>{selected.contact}</strong></div>}
          {selected.email && <a href={`mailto:${selected.email}`}>{selected.email}</a>}
          {selected.phone && <a href={`tel:${selected.phone}`}>{selected.phone}</a>}
          {selected.metrics?.length ? <section className="mt-5 grid w-full grid-cols-3 border border-gray-300 bg-gray-100" aria-label={`${selected.name} metrics`}>
            {selected.metrics.map(metric => <span className="grid min-w-0 gap-1 border-b border-r border-gray-300 bg-gray-50 p-3" key={metric.label}><small className="truncate text-[7px] font-extrabold uppercase tracking-wide text-gray-500">{metric.label}</small><strong className="text-sm text-emerald-700">{metric.value ?? 0}</strong></span>)}
          </section> : null}
          <button onClick={() => onOpen(selected.detailPath)}>Open details <b>→</b></button>
          {(selected.email || selected.phone) && <a
            className="entity-map-contact-action"
            href={selected.email ? `mailto:${selected.email}` : `tel:${selected.phone}`}
          >Contact {contactEntityLabel} <b>→</b></a>}
        </> : <p>Select a marker to view details.</p>}
        {geocoding > 0 ? <footer>Locating {geocoding} address{geocoding === 1 ? '' : 'es'}… {mapped.length} of {locatedItems.length} shown</footer> : <footer>{mapped.length} of {locatedItems.length} location{locatedItems.length === 1 ? '' : 's'} shown{missingAddressCount ? ` · ${missingAddressCount} missing ${addressLabel}${missingAddressCount === 1 ? '' : 'es'}` : ''}{unlocatedAddressCount ? ` · ${unlocatedAddressCount} ${addressLabel}${unlocatedAddressCount === 1 ? '' : 'es'} could not be located` : ''}</footer>}
      </aside>
    </div>
  );
};
